import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import { DetailSection } from "../detailBits";
import { SubmissionPackagePanel } from "../SubmissionPackagePanel";
import { SendHistory } from "../sendHistory/SendHistory";

/**
 * The send that goes to the LISTING BROKER, and the letter it carries.
 *
 * <p>Second of the two sends, and the consequential one: this is Phoenix
 * approaching a third party on the buyer's behalf, and it cannot be taken back
 * by editing a row. The intro email that precedes it has its own tab because it
 * has a different recipient, a different purpose, and — the fact that settles
 * the split — different entitlement semantics: sending THIS moves
 * <c>PackageSentAtUtc</c>, and sending the intro email deliberately moves
 * nothing.</p>
 *
 * <p>🔴 <b>The letter selector travels with the package — and now with the
 * DOCUMENT.</b> It chooses which prequalification letter this package encloses,
 * so it lives inside that document's own card rather than in a section beneath
 * the whole panel, where it read as a setting about the submission and sat four
 * documents away from the card describing the very letter it selects. Every
 * option names the LISTING it was sized against — a letter sized for a $2M
 * business attached to a $600K submission is what it exists to prevent.</p>
 *
 * <p>🔴 <b><c>canWrite</c> arrives as a prop.</b> The package panel offered
 * Generate / Send to broker / Edit narrative to every read-only role until it
 * was gated — a package sent to a listing broker is about the most consequential
 * write on this record to have been left open — and the letter override was
 * hard-coded <c>true</c>. Neither computes its own answer.</p>
 */
export function SubmissionPackageTab({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  return (
    <div className="grid gap-space-5">
      {/* buyerLeadId gives the blocked state's call to action somewhere to send
          the specialist — the package read does not carry the buyer, but this
          page already has it. */}
      <SubmissionPackagePanel
        submissionId={s.id}
        buyerLeadId={s.buyerLeadId}
        prequalificationLetterOverrideSubmissionId={
          s.prequalificationLetterOverrideSubmissionId
        }
        canEdit={canWrite}
      />

      {/* Below the panel that holds the letter selector, because the selector
          governs the NEXT package and this records the ones already sent. Each
          version names the letter it enclosed, which is the fact the selector
          would otherwise silently rewrite: that column holds the current
          choice, so without the version rows a change to it makes every past
          package appear to have carried the new letter. */}
      <DetailSection title="Sent to the listing broker">
        <SendHistory submissionId={s.id} kind="Package" canWrite={canWrite} />
      </DetailSection>

      {/* 🔴 A PARALLEL track, not an exception to the one above. The same
          document goes to two sides of the transaction; either send may happen
          first and neither requires the other, so two cards of the same shape
          read correctly where one card with a footnote would not.

          Only the broker card's sends move PackageSentAtUtc — the header badge,
          the status stepper and what the specialist has earned all read that
          column and none of them reads this one. */}
      <DetailSection title="Sent to the buyer">
        <SendHistory submissionId={s.id} kind="PackageToBuyer" canWrite={canWrite} />
      </DetailSection>
    </div>
  );
}
